import type { AlertOptions, UseIonAlertResult } from '@ionic/react';
import type { HookOverlayOptions } from '@ionic/react/dist/types/hooks/HookOverlayOptions';
import { SplashScreen } from '@capacitor/splash-screen';
import { Logger } from './support/Logger';
import { UserFacingError } from './innerhive-plus/revenue-cat/UserFacingError';
import { NetworkAdapter } from './support/NetworkAdapter';

export type AlertButtonOptions = string | { text: string,  handler: () => boolean | void | Promise<void> };

export interface IAlertOptions {
    header: string;
    subHeader?: string;
    message: string;
    buttons?: AlertButtonOptions[];
    backdropDismiss?: boolean;
    drawerAlert?: boolean;
    wider?: boolean;
    moreButtons?: boolean;
    // for an alert whose text names someone, which session replay would otherwise record
    maskInReplay?: boolean;
}

let __showAlert: UseIonAlertResult[0] | undefined;

/** Called by AlertBinder, which renders before the IOC container exists and so cannot be injected. */
export const bindAlertPresenter = (showAlert: UseIonAlertResult[0]) => {
    __showAlert = showAlert;
};

/**
 * This has minimal IOC dependencies, so it can be used throughout app start.
 *
 * If additional dependencies are needed, that aren't available early in app start, b/c
 * they are registered specially, in AppContainer, then use property injection and handle
 * that they might not exist
 */
export class AlertPresenter {
    static inject = () => [
        NetworkAdapter,
        Logger
    ];
    constructor(
        private network: NetworkAdapter,
        private logger: Logger
    ) {}

    // Whether an alert is open. Ionic's hook draws one at a time and silently drops a present while
    // one is open, and two presents in one tick stack, which several requests failing at once did.
    private alertOpen = false;

    private _showAlert = (options: AlertOptions & HookOverlayOptions) => {
        if (!__showAlert){
            throw new Error(`AlertPresenter was used before AlertBinder was rendered`);
        }
        return __showAlert(options);
    };

    showAlert = (options: IAlertOptions) => {
        this.present(options);
    };

    private present = ({
        header,
        subHeader,
        message,
        drawerAlert = false,
        buttons = ['OK'],
        backdropDismiss = false,
        wider = false,
        // if more than 2 buttons
        moreButtons = false,
        maskInReplay = false,
        onDidDismiss
    }: IAlertOptions & { onDidDismiss?: () => void }) => {
        SplashScreen.hide();
        this.alertOpen = true;

        const cssClass: string[] = [];

        if (maskInReplay) {
            cssClass.push('mp-mask');
        }

        if (wider) {
            cssClass.push('wider-alert');
        }

        if (drawerAlert) {
            cssClass.push('drawer-alert');
        }

        if (moreButtons) {
            cssClass.push('more-buttons-alert');
        }

        // an action sheet might be animating away, so give it a chance to dissapear
        // or else our alert never shows up
        // NOTE: it also could be tied to another alert animating away but I am 99% that is not the case
        // NOTE: we could explore a better solution that actually talks to some sort of ionic
        // action sheet controller and can check there or even await...
        // NOTE: ionicShowAlert() below is in fact async, and awaits the animation internally,
        // but the way we invoke alerts doesn't make it easy to await on them
        setTimeout(async () => {
            try {
                await this._showAlert({
                    message,
                    subHeader,
                    buttons,
                    header,
                    backdropDismiss,
                    cssClass: cssClass.join(' '),
                    onDidDismiss: () => {
                        this.alertOpen = false;
                        onDidDismiss?.();
                    }
                });
            } catch (err) {
                this.alertOpen = false;
                this.logger.error(err);
            }
        }, 100); // 10 wasn't long enough in my exploratory testing, so went with 100 to be safe
    };

    showAndLogError = (unknownError: any) => {
        // showAndLogError is often used in a catch, where error is unkown
        // we don't expect any errors to be undefined (though catch has to embrace that possiblility)
        const error = unknownError as Error & { status?: number, displayMessage?: string };

        // do log part...
        if ((error?.status || 500) >= 500) {
            // log errors with 500 status or no status so the caller of this method doesn't need to remember to log
            this.logger.error(error);
        }

        if (unknownError && unknownError.errorType === 'UserFacingError'){
            return this.showUserFacingError(unknownError as UserFacingError);
        }

        // An alert is already in front of the person.
        if (this.alertOpen) {
            return;
        }

        const requestFailed = unknownError?.errorCode?.startsWith('offline') || unknownError?.errorCode === 'fetch.failed';

        if (!this.network.isOnline) {
            return this.showAlert({
                header: `It looks like you're offline`,
                message: `Please try again when you have reliable internet.`
            });
        }

        if (requestFailed) {
            return this.showAlert({
                header: `We couldn't reach Innerhive`,
                message: 'Please check your connection and try again.'
            });
        }

        return this.showAlert({
            header: 'Sorry! Something went wrong',
            message: error?.displayMessage || `We've been notified about this problem, and are looking into it. Please try again.`
        });
    };

    private showUserFacingError = async (error: UserFacingError) => {
        const result = await this.showAlertV2({
            header: error.displayHeader || 'Sorry! Something went wrong',
            message: error.displayMessage,
            options: [
                ...error.links.map(({ label }) => ({ label, id: label })),
                {label: error.okLabel || 'OK', id: 'ok'}
            ],
        });
        if (result !== 'ok'){
            const chosenLink = error.links.find(l => l.label === result);
            if (chosenLink){
                window.open(chosenLink.href);
            }
        }
    };

    showAlertV2 = async ({
        header,
        subHeader,
        message = '',
        drawerAlert,
        backdropDismiss,
        options = [{label: 'OK', id: 'ok'}],
        wider,
        maskInReplay
    }: {
        header: string;
        subHeader?: string;
        message?: string;
        drawerAlert?: boolean;
        backdropDismiss?: boolean;
        options?: { label: string, id: string, role?: string, handler?: () => void }[];
        wider?: boolean;
        maskInReplay?: boolean;
    }): Promise<string> => {
        // Resolved once the alert has gone, not when its button is tapped: an alert the caller
        // raises while this one is still closing is dropped (see alertOpen), and an action sheet
        // closing alongside it reloads the page.
        return new Promise(resolve => {
            let chosen: { id: string; handler?: () => void } | undefined;
            this.present({
                header,
                subHeader,
                message,
                drawerAlert,
                backdropDismiss,
                wider,
                maskInReplay,
                // if more than 2 buttons
                moreButtons: options.length > 2,
                buttons: options.map(({ id, label, role, handler }) => ({
                    text: label,
                    role: role,
                    handler: () => {
                        chosen = { id, handler };
                    }
                })),
                onDidDismiss: () => {
                    if (!chosen) {
                        return;
                    }
                    resolve(chosen.id);
                    try {
                        chosen.handler?.();
                    } catch (err) {
                        // Ionic forgets the alert only after this returns, and would drop every later one.
                        this.logger.error(err);
                    }
                }
            });
        });
    };

    confirmDestructiveAction = async ({
        message,
        header,
        drawerAlert,
        destroyLabel = 'Delete',
        cancelLabel = 'Cancel',
        wider
    }: {
        message: string;
        header: string;
        drawerAlert?: boolean;
        destroyLabel?: string;
        cancelLabel?: string;
        wider?: boolean;
    }): Promise<'destroy' | 'cancel'> => {
        return this.showAlertV2({
            header,
            message,
            drawerAlert,
            wider,
            options: [{
                id: 'destroy',
                label: destroyLabel,
                role: 'destructive'
            }, {
                id: 'cancel',
                label: cancelLabel,
                role: 'cancel'
            }] }) as Promise<'destroy' | 'cancel'>;
    };

    confirmCancelAction = async ({ message, header, cancelNoun }: { message: string, header: string, cancelNoun: string }): Promise<'destroy' | 'cancel'> => {
        return this.confirmDestructiveAction({message, header, destroyLabel:`Cancel ${cancelNoun}`, cancelLabel: 'Not Now'});
    };
}
